import React from 'react';
import type { MovementType, TransactionStatus, TransactionType } from '@seccl/client';

/*
 * Viewed Transaction context.
 * --------------------------------------------------------------------------
 * Tracks the transaction currently being viewed — either via the v2 table
 * side panel or the /transactions/[transactionId] detail page. This context
 * is INERT with respect to Quick Actions: it has no dependency on
 * `@seccl/quick-actions`, so it is safe to render unconditionally in _app.tsx.
 *
 * The Quick Actions factories (gated, non-prod only) close over the getter
 * returned by `useViewedTransaction` to drive their `isAvailable` predicates
 * and auto-prefill inputs.
 */

export interface ViewedTransaction {
  id: string;
  transactionType: TransactionType;
  movementType: MovementType;
  status: TransactionStatus;
  assetId?: string;
  amount?: number;
  quantity?: number;
}

interface ViewedTransactionStore {
  current: ViewedTransaction | null;
  set: (tx: ViewedTransaction | null) => void;
}

const ViewedTransactionContext = React.createContext<ViewedTransactionStore | null>(null);

export const ViewedTransactionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [current, setCurrent] = React.useState<ViewedTransaction | null>(null);

  const value = React.useMemo<ViewedTransactionStore>(() => ({ current, set: setCurrent }), [current]);

  return <ViewedTransactionContext.Provider value={value}>{children}</ViewedTransactionContext.Provider>;
};

/** Returns the currently viewed transaction, or null when none is active. */
export const useViewedTransaction = (): ViewedTransaction | null => {
  return React.useContext(ViewedTransactionContext)?.current ?? null;
};

/** Returns a stable setter for the currently viewed transaction. */
export const useSetViewedTransaction = (): ((tx: ViewedTransaction | null) => void) => {
  const ctx = React.useContext(ViewedTransactionContext);
  return ctx?.set ?? (() => undefined);
};
